import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  getProposedActions,
  approveProposedAction,
  dismissProposedAction,
} from "../../api/analyticsApi";

// AI proposes → owner approves → the existing executor runs → outcome tracked.
// Renders where the owner already looks (dashboard + Insights tab). Approve/
// dismiss are role-gated server-side; the button hides when the viewer can't act.

const STATUS_LABEL = {
  proposed: "Awaiting approval",
  queued: "Queued — executes unless vetoed",
  vetoed: "Vetoed",
  executing: "Executing…",
  executed: "Executed",
  rejected: "Dismissed",
  expired: "Expired",
  failed: "Failed",
};

const TIER_LABEL = {
  auto_execute: "AUTO",
  queue_with_veto_window: "VETO WINDOW",
  always_approve: "HUMAN APPROVAL",
};

function vetoCountdown(vetoExpiresAt) {
  if (!vetoExpiresAt) return null;
  const ms = new Date(vetoExpiresAt).getTime() - Date.now();
  if (ms <= 0) return "window closed";
  const h = Math.floor(ms / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  return h > 0 ? `executes in ${h}h ${m}m unless vetoed` : `executes in ${m}m unless vetoed`;
}

function OutcomeCounters({ counts }) {
  if (!counts) return null;
  const order = ["reply", "interested", "booking", "opt_out", "silence", "delivery_failed"];
  const labels = { reply: "Replies", interested: "Interested", booking: "Bookings", opt_out: "Opt-outs", silence: "Silence", delivery_failed: "Delivery fails" };
  return (
    <div className="pa-outcome-counters" title="Real observed outcomes — zeros until live sends produce them. Sandboxed sends never count.">
      {order.map((k) => (
        <span key={k} className={`pa-counter ${counts[k] > 0 ? "pa-counter--nonzero" : ""}`}>
          {labels[k]} {counts[k] ?? 0}
        </span>
      ))}
    </div>
  );
}

function pct(value) {
  const n = Number(value || 0);
  return `${Math.round(n * 100)}%`;
}

function CustomerName({ c }) {
  const label = c.name || c.external_id;
  // Every listed customer is a doorway: CRM-linked → customer card;
  // FR-only → the FieldRoutes detail route. Plain text only when we have
  // no identifier at all.
  if (c.customer_id) {
    return <Link className="pa-target__name" to={`/customers/${c.customer_id}`}>{label}</Link>;
  }
  if (c.external_id) {
    return <Link className="pa-target__name" to={`/customers/fr/${c.external_id}`}>{label}</Link>;
  }
  return <strong>{label}</strong>;
}

function WeightProvenance({ c }) {
  // "Why is this ranked here" — learned-weight citations. Hidden while every
  // weight is neutral (the honest zero-outcome regime).
  const matched = c.matched_weights || {};
  const keys = Object.keys(matched);
  const product = Number(c.weight_product || 1);
  if (!keys.length || Math.abs(product - 1) < 0.0001) return null;
  return (
    <div className="pa-target__weights">
      weight ×{product.toFixed(2)}
      {keys.slice(0, 3).map((k) => {
        const w = matched[k];
        return (
          <span key={k} className="pa-chip pa-chip--weight">
            {k} ×{Number(w.weight).toFixed(2)} — {w.positive}▲ {w.negative}▼
          </span>
        );
      })}
    </div>
  );
}

function TargetEvidence({ c }) {
  // Profile-cited evidence when the contextualization engine has read this
  // customer; falls back to the raw churn signals when unprofiled.
  const prof = c.profile;
  if (!prof) {
    const reasonLabel = {
      not_linked_to_crm: "no CRM record linked yet",
      not_profiled: "not yet profiled",
      thin_history: "thin history — low-confidence profile",
    }[c.no_evidence_reason];
    return (
      <div>
        <span className="pa-target__fallback">
          risk {pct(c.risk_score)} · {c.risk_reason}
          {reasonLabel && <em> · {reasonLabel}</em>}
        </span>
        <WeightProvenance c={c} />
      </div>
    );
  }
  const complaints = Array.isArray(prof.unresolved_complaints) ? prof.unresolved_complaints : [];
  const citations = Array.isArray(prof.citations) ? prof.citations : [];
  return (
    <div className="pa-target__profile">
      {prof.health_summary && <div className="pa-target__health">{prof.health_summary}</div>}
      {complaints.length > 0 && (
        <div className="pa-target__complaints">
          {complaints.slice(0, 2).map((topic, i) => (
            <span key={i} className="pa-chip pa-chip--complaint">{topic}</span>
          ))}
        </div>
      )}
      {citations.length > 0 && (
        <ul className="pa-citations">
          {citations.slice(0, 3).map((cit, i) => (
            <li key={i}>
              “{cit.claim}”
              <span className="pa-citation__src"> — {cit.source}{cit.date ? `, ${cit.date}` : ""}</span>
            </li>
          ))}
        </ul>
      )}
      <WeightProvenance c={c} />
    </div>
  );
}

function Evidence({ evidence }) {
  const customers = Array.isArray(evidence?.customers) ? evidence.customers : [];
  if (!customers.length) return null;
  const profiled = Number(evidence.profiled || 0);
  return (
    <details className="pa-evidence" open>
      <summary>
        Why these {evidence.cohort_size} customers · avg risk {pct(evidence.avg_risk_score)}
        {profiled > 0 && ` · ${profiled} with cited evidence`}
        {evidence.no_evidence_breakdown && (
          (() => {
            const b = evidence.no_evidence_breakdown;
            const parts = [];
            if (b.not_linked_to_crm) parts.push(`${b.not_linked_to_crm} awaiting CRM link`);
            if (b.not_profiled) parts.push(`${b.not_profiled} not yet profiled`);
            if (b.thin_history) parts.push(`${b.thin_history} thin history`);
            return parts.length ? ` · ${parts.join(" · ")}` : "";
          })()
        )}
      </summary>
      <ol className="pa-targets">
        {customers.slice(0, 50).map((c) => (
          <li key={c.external_id} className="pa-target">
            <div className="pa-target__head">
              <CustomerName c={c} />
              <span className="pa-target__risk">risk {pct(c.risk_score)}</span>
            </div>
            <TargetEvidence c={c} />
          </li>
        ))}
        {customers.length > 50 && <li>…and {customers.length - 50} more</li>}
      </ol>
    </details>
  );
}

function Outcome({ result }) {
  const outcome = result?.outcome;
  if (!outcome) return null;
  return (
    <div className="pa-outcome">
      <span className={`pa-badge ${outcome.sandboxed ? "pa-badge--sandbox" : "pa-badge--live"}`}>
        {outcome.sandboxed ? "SANDBOXED — not sent" : "LIVE"}
      </span>
      <span>Reached {outcome.reached}</span>
      <span>Replies {outcome.replies}</span>
      <span>Interested {outcome.interested}</span>
      <span>Booked {outcome.appointments_booked}</span>
    </div>
  );
}

export default function ProposedActionsCard({ compact = false }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState("");
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await getProposedActions();
      setItems(Array.isArray(data?.proposed_actions) ? data.proposed_actions : []);
      setError("");
    } catch (e) {
      setError("Could not load proposed actions.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const act = async (id, fn) => {
    setBusyId(id);
    try {
      await fn(id);
      await load();
    } catch (e) {
      const detail = e?.response?.data?.error || "Action failed.";
      setError(detail === "insufficient_role" ? "You don't have permission to approve this." : detail);
    } finally {
      setBusyId("");
    }
  };

  if (loading) return <div className="pa-card pa-card--muted">Loading AI proposals…</div>;
  if (!items.length) {
    return compact ? null : (
      <div className="pa-card pa-card--muted">No AI proposals right now.</div>
    );
  }

  return (
    <div className="pa-card">
      <div className="pa-card__head">
        <h3>AI Proposals</h3>
        <span className="pa-count">{items.length}</span>
      </div>
      {error && <div className="pa-error">{error}</div>}
      <ul className="pa-list">
        {items.map((item) => (
          <li key={item.id} className={`pa-item pa-item--${item.status}`}>
            <div className="pa-item__title">
              {item.title}
              <span className={`pa-tier pa-tier--${item.autonomy_tier}`}>
                {TIER_LABEL[item.autonomy_tier] || item.autonomy_tier}
              </span>
              {item.executed_by_system && <span className="pa-tier pa-tier--system">EXECUTED BY SYSTEM</span>}
            </div>
            <div className="pa-item__reason">{item.reason}</div>
            {item.status === "queued" && (
              <div className="pa-veto-countdown">{vetoCountdown(item.veto_expires_at)}</div>
            )}
            <Evidence evidence={item.evidence} />
            <OutcomeCounters counts={item.outcome_counts} />
            {["proposed", "queued"].includes(item.status) && item.can_approve && (
              <div className="pa-actions">
                <button
                  className="pa-btn pa-btn--approve"
                  disabled={busyId === item.id}
                  onClick={() => act(item.id, approveProposedAction)}
                >
                  {busyId === item.id ? "Approving…" : "Approve & send"}
                </button>
                <button
                  className="pa-btn pa-btn--dismiss"
                  disabled={busyId === item.id}
                  onClick={() => act(item.id, dismissProposedAction)}
                >
                  {item.status === "queued" ? "Veto" : "Dismiss"}
                </button>
              </div>
            )}
            {["proposed", "queued"].includes(item.status) && !item.can_approve && (
              <div className="pa-note">Requires {item.approver_role} approval.</div>
            )}
            {!["proposed", "queued"].includes(item.status) && (
              <div className="pa-status">{STATUS_LABEL[item.status] || item.status}</div>
            )}
            <Outcome result={item.result} />
          </li>
        ))}
      </ul>
    </div>
  );
}
